import React, { useState } from "react";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: "1.25rem",
  },
  cardHead: {
    padding: "0.75rem 1.125rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1d23",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardBody: { padding: "1rem 1.125rem" },
  kpiCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1rem 1.125rem",
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 6,
  },
  kpiValue: { fontSize: 22, fontWeight: 700, lineHeight: 1, color: "#1a1d23" },
  kpiSub: { fontSize: 12, color: "#9ca3af", marginTop: 5 },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 2,
  },
  fieldValue: { fontSize: 13, color: "#1a1d23" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
  },
  pill: (variant) => {
    const map = {
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
      red: { background: "#FEF2F2", color: "#991B1B" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  btn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnGreen: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 6,
    border: "1px solid #BBF7D0",
    background: "transparent",
    color: "#166534",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 14px",
    borderRadius: 6,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
  },
  btnLink: {
    fontSize: 11,
    color: "#6b7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline",
  },
  input: {
    fontSize: 13,
    padding: "5px 9px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    color: "#1a1d23",
    background: "#fff",
    width: 80,
    outline: "none",
  },
  inputFull: {
    fontSize: 13,
    padding: "6px 10px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    color: "#1a1d23",
    background: "#fff",
    width: "100%",
    outline: "none",
    boxSizing: "border-box",
  },
  select: {
    fontSize: 13,
    padding: "6px 10px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    color: "#1a1d23",
    background: "#fff",
    width: "100%",
    outline: "none",
    boxSizing: "border-box",
  },
  formLabel: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 4,
  },
  errorBanner: {
    fontSize: 12,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "8px 12px",
    marginBottom: "0.875rem",
  },
  infoBanner: {
    fontSize: 12,
    color: "#92400E",
    background: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: 8,
    padding: "8px 12px",
    marginBottom: "0.875rem",
  },
  spinner: {
    display: "inline-block",
    width: 12,
    height: 12,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 5,
  },
  // Modal styles
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1060,
    padding: "1rem",
  },
  modal: {
    background: "#fff",
    borderRadius: 12,
    width: "100%",
    maxWidth: 480,
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  modalHead: {
    padding: "1rem 1.25rem",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 15,
    fontWeight: 600,
    color: "#1a1d23",
  },
  modalBody: { padding: "1.25rem" },
  modalFoot: {
    padding: "0.875rem 1.25rem",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    background: "#fafafa",
  },
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Spinner = () => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <span style={s.spinner} />
  </>
);

const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const FieldRow = ({ label, value }) => (
  <div>
    <div style={s.fieldLabel}>{label}</div>
    <div style={s.fieldValue}>{value ?? "—"}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "6px 0",
      borderBottom: "1px solid #f3f4f6",
      fontSize: 13,
    }}
  >
    <span style={{ color: "#6b7280" }}>{label}</span>
    <span style={{ fontWeight: 500 }}>{value}</span>
  </div>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatCurrency = (n) =>
  new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);

const formatDate = (date) => {
  if (!date) return "Not set";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// ── Main component ────────────────────────────────────────────────────────────
const SaleFinancials = ({ property, onPropertyUpdated }) => {
  const [editingCommission, setEditingCommission] = useState(false);
  const [rateInput, setRateInput] = useState("");
  const [paidByInput, setPaidByInput] = useState("seller");
  const [savingCommission, setSavingCommission] = useState(false);
  const [commissionError, setCommissionError] = useState("");
  const [markingSold, setMarkingSold] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [actionError, setActionError] = useState("");
  const [saleDateInput, setSaleDateInput] = useState(() => new Date().toISOString().slice(0, 10));
  const [showMarkSoldModal, setShowMarkSoldModal] = useState(false);
  const [buyerNameInput, setBuyerNameInput] = useState("");
  const [buyerAgentInput, setBuyerAgentInput] = useState("");
  const [buyerContactInput, setBuyerContactInput] = useState("");
  const [markSoldModalError, setMarkSoldModalError] = useState("");
  const { getAuthHeaders } = useAuth();

  if (!property) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", fontSize: 14, color: "#6b7280" }}>
        Property data not available
      </div>
    );
  }

  // ── Derived data ──────────────────────────────────────────────────────────
  const salePrice = property.pricing?.salePrice || 0;
  const commission = property.pricing?.saleCommission || {};
  const saleInfo = property.saleInfo || {};
  const buyerInfo = saleInfo.buyerInfo || {};
  const sellerInfo = saleInfo.sellerInfo || {};

  const getCommissionDisplay = () => {
    if (commission.type === "percentage") return `${commission.rate}% of Sale Price`;
    if (commission.type === "flat_fee") return "Flat Fee";
    return "Not specified";
  };

  const getCommissionAmount = () => {
    if (commission.amount != null && commission.amount >= 0) return commission.amount;
    if (commission.type === "percentage" && commission.rate && salePrice)
      return (salePrice * commission.rate) / 100;
    return 0;
  };

  const getStatusPill = () => {
    const status = property.status;
    if (status === "sold") return <Pill variant="green">Sold</Pill>;
    if (status === "under_contract") return <Pill variant="amber">Under Contract</Pill>;
    if (status === "available") return <Pill variant="blue">Available</Pill>;
    if (status === "withdrawn") return <Pill variant="gray">Withdrawn</Pill>;
    return <Pill variant="gray">Unknown</Pill>;
  };

  const commissionAmount = getCommissionAmount();
  const canMarkSold =
    (property.status === "available" || property.status === "under_contract") && !saleInfo.isSold;
  const isSoldRecorded = property.status === "sold" && saleInfo.isSold;
  const commissionAmt = getCommissionAmount();
  const showMarkPaid = isSoldRecorded && !commission.isPaid && commissionAmt > 0;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleEditCommission = () => {
    const c = property.pricing?.saleCommission || {};
    setRateInput(c.rate != null ? String(c.rate) : "");
    setPaidByInput(c.paidBy || "seller");
    setCommissionError("");
    setEditingCommission(true);
  };

  const handleSaveCommission = async () => {
    const parsed = parseFloat(rateInput, 10);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      setCommissionError("Enter a percentage between 0 and 100.");
      return;
    }
    setSavingCommission(true);
    setCommissionError("");
    try {
      const res = await fetch(API_ENDPOINTS.properties.update(property._id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          pricing: {
            ...property.pricing,
            saleCommission: {
              ...property.pricing?.saleCommission,
              type: "percentage",
              rate: parsed,
              paidBy: paidByInput,
            },
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(data?.message) ? data.message.join(", ") : data?.message;
        throw new Error(msg || "Failed to update commission");
      }
      onPropertyUpdated?.(data.data || data);
      setEditingCommission(false);
    } catch (err) {
      console.error(err);
      setCommissionError(err.message || "Could not save commission.");
    } finally {
      setSavingCommission(false);
    }
  };

  const openMarkSoldModal = () => {
    setMarkSoldModalError("");
    setActionError("");
    setBuyerNameInput((saleInfo.buyerInfo?.buyerName || "").trim());
    setBuyerAgentInput((saleInfo.buyerInfo?.buyerAgent || "").trim());
    setBuyerContactInput((saleInfo.buyerInfo?.buyerContact || "").trim());
    setSaleDateInput(new Date().toISOString().slice(0, 10));
    setShowMarkSoldModal(true);
  };

  const handleMarkSold = async () => {
    if (!property._id || markingSold) return;
    const name = buyerNameInput.trim();
    const contact = buyerContactInput.trim();
    if (!name) { setMarkSoldModalError("Enter the buyer's name."); return; }
    if (!contact) { setMarkSoldModalError("Enter the buyer's phone or email."); return; }
    setMarkSoldModalError("");
    setActionError("");
    setMarkingSold(true);
    try {
      const res = await fetch(API_ENDPOINTS.properties.markSold(property._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          saleDate: saleDateInput || undefined,
          buyerName: name,
          buyerAgent: buyerAgentInput.trim(),
          buyerContact: contact,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(data?.message) ? data.message.join(", ") : data?.message;
        throw new Error(msg || "Could not mark property as sold");
      }
      onPropertyUpdated?.(data.data || data);
      setShowMarkSoldModal(false);
    } catch (err) {
      console.error(err);
      const msg = err.message || "Could not mark as sold.";
      setMarkSoldModalError(msg);
      setActionError(msg);
    } finally {
      setMarkingSold(false);
    }
  };

  const handleMarkCommissionPaid = async () => {
    if (!property._id || markingPaid || !showMarkPaid) return;
    setActionError("");
    setMarkingPaid(true);
    try {
      const res = await fetch(API_ENDPOINTS.properties.markSaleCommissionPaid(property._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(data?.message) ? data.message.join(", ") : data?.message;
        throw new Error(msg || "Could not mark commission paid");
      }
      onPropertyUpdated?.(data.data || data);
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Could not mark commission paid.");
    } finally {
      setMarkingPaid(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Mark-sold modal ── */}
      {showMarkSoldModal && (
        <div
          style={s.overlay}
          onClick={() => { if (!markingSold) setShowMarkSoldModal(false); }}
        >
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHead}>
              Record sale &amp; buyer
              {!markingSold && (
                <button
                  style={{ ...s.btnLink, fontSize: 18, lineHeight: 1 }}
                  onClick={() => setShowMarkSoldModal(false)}
                >
                  ×
                </button>
              )}
            </div>
            <div style={s.modalBody}>
              <p style={{ fontSize: 12, color: "#6b7280", marginBottom: "0.875rem", lineHeight: 1.6 }}>
                Enter the sale date and buyer details. This is saved on the property and shown
                under Buyer Information.
              </p>
              {markSoldModalError && (
                <div style={s.errorBanner}>{markSoldModalError}</div>
              )}
              <div style={{ marginBottom: "0.875rem" }}>
                <label style={s.formLabel}>Sale date</label>
                <input
                  style={s.inputFull}
                  type="date"
                  value={saleDateInput}
                  onChange={(e) => setSaleDateInput(e.target.value)}
                  disabled={markingSold}
                />
              </div>
              <div style={{ marginBottom: "0.875rem" }}>
                <label style={s.formLabel}>Buyer name <span style={{ color: "#ef4444" }}>*</span></label>
                <input
                  style={s.inputFull}
                  type="text"
                  value={buyerNameInput}
                  onChange={(e) => setBuyerNameInput(e.target.value)}
                  placeholder="Full name"
                  disabled={markingSold}
                  autoComplete="name"
                />
              </div>
              <div style={{ marginBottom: "0.875rem" }}>
                <label style={s.formLabel}>Buyer's agent / brokerage <span style={{ color: "#9ca3af" }}>(optional)</span></label>
                <input
                  style={s.inputFull}
                  type="text"
                  value={buyerAgentInput}
                  onChange={(e) => setBuyerAgentInput(e.target.value)}
                  placeholder="Agent or company name"
                  disabled={markingSold}
                />
              </div>
              <div>
                <label style={s.formLabel}>Buyer contact <span style={{ color: "#ef4444" }}>*</span></label>
                <input
                  style={s.inputFull}
                  type="text"
                  value={buyerContactInput}
                  onChange={(e) => setBuyerContactInput(e.target.value)}
                  placeholder="Phone or email"
                  disabled={markingSold}
                  autoComplete="tel"
                />
              </div>
            </div>
            <div style={s.modalFoot}>
              <button
                style={s.btn}
                onClick={() => setShowMarkSoldModal(false)}
                disabled={markingSold}
              >
                Cancel
              </button>
              <button style={s.btnPrimary} onClick={handleMarkSold} disabled={markingSold}>
                {markingSold ? (
                  <>
                    <Spinner />
                    Saving…
                  </>
                ) : (
                  "Confirm sale"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── KPI strip ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 10,
          marginBottom: "1.25rem",
        }}
      >
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Sale price</div>
          <div style={{ ...s.kpiValue, color: "#1E40AF" }}>{formatCurrency(salePrice)}</div>
          <div style={s.kpiSub}>asking price</div>
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Commission rate</div>
          {editingCommission ? (
            <div style={{ marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <input
                  style={s.input}
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={rateInput}
                  autoFocus
                  onChange={(e) => setRateInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveCommission();
                    if (e.key === "Escape") setEditingCommission(false);
                  }}
                />
                <span style={{ fontSize: 13, color: "#6b7280" }}>%</span>
              </div>
              <select
                style={{ ...s.select, width: "auto", marginBottom: 6 }}
                value={paidByInput}
                onChange={(e) => setPaidByInput(e.target.value)}
              >
                <option value="seller">Paid by seller</option>
                <option value="buyer">Paid by buyer</option>
                <option value="split">Split</option>
              </select>
              {commissionError && (
                <div style={{ fontSize: 11, color: "#991B1B", marginBottom: 6 }}>
                  {commissionError}
                </div>
              )}
              <div style={{ display: "flex", gap: 6 }}>
                <button style={s.btnPrimary} disabled={savingCommission} onClick={handleSaveCommission}>
                  {savingCommission ? (
                    <>
                      <Spinner />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
                <button style={s.btn} disabled={savingCommission} onClick={() => setEditingCommission(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ ...s.kpiValue, color: "#0F6E56" }}>
                {commission.type === "percentage" && commission.rate != null
                  ? `${commission.rate}%`
                  : commission.type === "flat_fee"
                    ? "Flat fee"
                    : "—"}
              </div>
              <div style={{ ...s.kpiSub, display: "flex", alignItems: "center", gap: 6 }}>
                {getCommissionDisplay()}
                <button
                  style={s.btnLink}
                  onClick={handleEditCommission}
                >
                  Edit
                </button>
              </div>
            </>
          )}
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Commission earned</div>
          <div style={{ ...s.kpiValue, color: "#166534" }}>{formatCurrency(commissionAmount)}</div>
          <div style={s.kpiSub}>{saleInfo.isSold ? "earned commission" : "potential earnings"}</div>
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Property status</div>
          <div style={{ marginTop: 6 }}>{getStatusPill()}</div>
          <div style={s.kpiSub}>current status</div>
        </div>
      </div>

      {/* ── Sale & commission actions ── */}
      <div style={s.card}>
        <div style={s.cardHead}>Sale &amp; commission</div>
        <div style={s.cardBody}>
          {actionError && <div style={s.errorBanner}>{actionError}</div>}
          <p style={{ fontSize: 12, color: "#6b7280", marginBottom: "0.875rem", lineHeight: 1.6 }}>
            When the sale closes, mark this listing as sold. Your commission is calculated from the
            sale price and rate, then recorded here. When you receive the commission, mark it as
            paid — it will appear in your payments and dashboard totals.
          </p>
          {canMarkSold && (
            <button style={s.btnPrimary} disabled={markingSold} onClick={openMarkSoldModal}>
              Mark as sold
            </button>
          )}
          {isSoldRecorded && saleInfo.saleDate && (
            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8, marginBottom: 0 }}>
              <strong>Sale date:</strong> {formatDate(saleInfo.saleDate)}
            </p>
          )}
          {isSoldRecorded && !commission.isPaid && commissionAmt <= 0 && (
            <div style={{ ...s.infoBanner, marginTop: 8, marginBottom: 0 }}>
              Set a sale price and commission rate above, then the earned amount will appear here.
            </div>
          )}
          {showMarkPaid && (
            <div
              style={{
                marginTop: "0.875rem",
                paddingTop: "0.875rem",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <p style={{ fontSize: 13, marginBottom: 10 }}>
                <strong>{formatCurrency(commissionAmt)}</strong> commission is pending. Mark paid
                when you receive it (creates a payment record).
              </p>
              <button style={s.btnGreen} disabled={markingPaid} onClick={handleMarkCommissionPaid}>
                {markingPaid ? (
                  <>
                    <Spinner />
                    Saving…
                  </>
                ) : (
                  "Mark commission paid"
                )}
              </button>
            </div>
          )}
          {isSoldRecorded && commission.isPaid && (
            <p style={{ fontSize: 12, color: "#166534", marginTop: 8, marginBottom: 0 }}>
              Commission recorded as paid and added to your payments.
            </p>
          )}
        </div>
      </div>

      {/* ── Commission structure + sale transaction details ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          marginBottom: "1.25rem",
        }}
      >
        {/* Commission structure */}
        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.cardHead}>Commission structure</div>
          <div style={s.cardBody}>
            <InfoRow label="Commission type" value={getCommissionDisplay()} />
            <InfoRow
              label="Commission amount"
              value={
                <span style={{ color: "#166534" }}>{formatCurrency(commissionAmount)}</span>
              }
            />
            {commission.type === "percentage" && commission.rate != null && (
              <InfoRow label="Rate" value={`${commission.rate}%`} />
            )}
            <InfoRow
              label="Paid by"
              value={
                <span style={{ textTransform: "capitalize" }}>
                  {commission.paidBy || "Not specified"}
                </span>
              }
            />
            <InfoRow
              label="Payment status"
              value={
                commission.isPaid ? (
                  <Pill variant="green">Paid</Pill>
                ) : (
                  <Pill variant="amber">Pending</Pill>
                )
              }
            />
            {commission.isPaid && saleInfo.commissionPaidDate && (
              <InfoRow label="Paid on" value={formatDate(saleInfo.commissionPaidDate)} />
            )}
          </div>
        </div>

        {/* Sale transaction details */}
        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.cardHead}>Sale transaction details</div>
          <div style={s.cardBody}>
            <InfoRow
              label="Property sold"
              value={
                saleInfo.isSold ? (
                  <Pill variant="green">Yes</Pill>
                ) : (
                  <Pill variant="gray">No</Pill>
                )
              }
            />
            <InfoRow
              label="Under contract"
              value={
                saleInfo.underContract ? (
                  <Pill variant="amber">Yes</Pill>
                ) : (
                  <Pill variant="gray">No</Pill>
                )
              }
            />
            {saleInfo.contractDate && (
              <InfoRow label="Contract date" value={formatDate(saleInfo.contractDate)} />
            )}
            {saleInfo.saleDate && (
              <InfoRow label="Sale date" value={formatDate(saleInfo.saleDate)} />
            )}
            {saleInfo.closingDate && (
              <InfoRow label="Closing date" value={formatDate(saleInfo.closingDate)} />
            )}
          </div>
        </div>
      </div>

      {/* ── Buyer information ── */}
      {(saleInfo.isSold || saleInfo.underContract) && (
        <div style={s.card}>
          <div style={s.cardHead}>Buyer information</div>
          <div style={s.cardBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "0.875rem",
              }}
            >
              <FieldRow
                label="Buyer name"
                value={
                  buyerInfo.buyerId?.personalInfo?.firstName &&
                  buyerInfo.buyerId?.personalInfo?.lastName
                    ? `${buyerInfo.buyerId.personalInfo.firstName} ${buyerInfo.buyerId.personalInfo.lastName}`
                    : buyerInfo.buyerName || "Not specified"
                }
              />
              <FieldRow label="Buyer agent" value={buyerInfo.buyerAgent || "Not specified"} />
              <FieldRow label="Contact" value={buyerInfo.buyerContact || "Not specified"} />
            </div>
          </div>
        </div>
      )}

      {/* ── Seller information ── */}
      {sellerInfo && (
        <div style={s.card}>
          <div style={s.cardHead}>Seller information</div>
          <div style={s.cardBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "0.875rem",
              }}
            >
              <FieldRow
                label="Seller name"
                value={sellerInfo.sellerName || "Not specified"}
              />
              <FieldRow
                label="Contact"
                value={sellerInfo.sellerContact || "Not specified"}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleFinancials;
